"use strict";
/** *******************************************************************************************************************
  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
  
  Licensed under the Apache License, Version 2.0 (the "License").
  You may not use this file except in compliance with the License.
  You may obtain a copy of the License at
  
      http://www.apache.org/licenses/LICENSE-2.0
  
  Unless required by applicable law or agreed to in writing, software
  distributed under the License is distributed on an "AS IS" BASIS,
  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
  See the License for the specific language governing permissions and
  limitations under the License.                                                                              *
 ******************************************************************************************************************** */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importStar(require("react"));
const clsx_1 = __importDefault(require("clsx"));
const core_1 = require("@material-ui/core");
const core_2 = require("@material-ui/core");
const IconButton_1 = __importDefault(require("@material-ui/core/IconButton"));
const Close_1 = __importDefault(require("@material-ui/icons/Close"));
const useUniqueId_1 = __importDefault(require("../../hooks/useUniqueId"));
const useStyles = core_1.makeStyles((theme) => ({
    body: {
        padding: '0.8rem 1rem',
    },
    'body-size-small': {
        maxWidth: '210px',
        '&.fixed-width': {
            width: '210px',
        },
    },
    'body-size-medium': {
        maxWidth: '310px',
        '&.fixed-width': {
            width: '310px',
        },
    },
    'body-size-large': {
        maxWidth: '460px',
        '&.fixed-width': {
            width: '460px',
        },
    },
    container: {
        zIndex: 2000,
    },
    content: {
        contentOverflowVisible: true,
        flex: '1 1 auto',
        color: theme.palette.grey['700'],
    },
    dismiss: {
        flex: '0 0 auto',
        fontSize: '20px',
        order: 1,
        padding: '0',
    },
    hasDismiss: {
        display: 'flex',
        alignItems: 'flex-start',
    },
    header: {
        flex: '1 1 auto',
        '& h2': {
            margin: 0,
            paddingBottom: '0.5em',
        },
    },
    trigger: {
        display: 'inline-block',
        color: 'inherit',
    },
    'trigger-type-text': {
        border: 0,
        margin: 0,
        padding: 0,
        backgroundColor: 'transparent',
        cursor: 'pointer',
        borderBottom: '1px dashed currentColor',
        '&:focus': {
            outline: 'none',
        },
    },
}));
/**
 * A Popover can be used to display some content on top of another.
 */
const Popover = (_a) => {
    var { position = 'right', showDismissButton = true, fixedWidth = false, size = 'medium', triggerType = 'text', variant = 'click', children, content, dismissAriaLabel, header, onOpen, onClose } = _a, restProps = __rest(_a, ["position", "showDismissButton", "fixedWidth", "size", "triggerType", "variant", "children", "content", "dismissAriaLabel", "header", "onOpen", "onClose"]);
    const labelledById = useUniqueId_1.default();
    const classes = useStyles({});
    const triggerRef = react_1.useRef(null);
    const [visible, setVisible] = react_1.useState(false);
    const onTriggerClick = react_1.useCallback(() => {
        setVisible(true);
        onOpen === null || onOpen === void 0 ? void 0 : onOpen();
    }, [onOpen]);
    const onPopoverClose = react_1.useCallback(() => {
        setVisible(false);
        onClose === null || onClose === void 0 ? void 0 : onClose();
    }, [onClose]);
    const mapPositionToMuiPopoverOrigins = react_1.useCallback(() => {
        let anchorOrigin, transformOrigin;
        switch (position) {
            case 'left':
                anchorOrigin = { vertical: 'center', horizontal: 'left' };
                transformOrigin = { vertical: 'center', horizontal: 'right' };
                break;
            case 'right':
                anchorOrigin = { vertical: 'center', horizontal: 'right' };
                transformOrigin = { vertical: 'center', horizontal: 'left' };
                break;
            case 'bottom':
                anchorOrigin = { vertical: 'bottom', horizontal: 'center' };
                transformOrigin = { vertical: 'top', horizontal: 'center' };
                break;
            default:
                anchorOrigin = { vertical: 'top', horizontal: 'center' };
                transformOrigin = { vertical: 'bottom', horizontal: 'center' };
                break;
        }
        return { anchorOrigin, transformOrigin };
    }, [position]);
    const triggerProps = {
        ref: triggerRef,
        onClick: variant !== 'hover' ? onTriggerClick : undefined,
        onMouseEnter: variant === 'hover' ? onTriggerClick : undefined,
        onMouseLeave: variant === 'hover' ? onPopoverClose : undefined,
        className: clsx_1.default(classes.trigger, classes[`trigger-type-${triggerType}`]),
    };
    const dismissButton = showDismissButton && (react_1.default.createElement(IconButton_1.default, { className: classes.dismiss, component: "button", "aria-label": dismissAriaLabel, onClick: onPopoverClose, "data-testid": "dismiss-button" },
        react_1.default.createElement(Close_1.default, { fontSize: "inherit" })));
    const { anchorOrigin, transformOrigin } = mapPositionToMuiPopoverOrigins();
    return (react_1.default.createElement("span", Object.assign({}, restProps),
        triggerType === 'text' ? (react_1.default.createElement("button", Object.assign({}, triggerProps, { type: "button", "aria-haspopup": "dialog" }),
            react_1.default.createElement("span", null, children))) : (react_1.default.createElement("span", Object.assign({}, triggerProps), children)),
        react_1.default.createElement("span", { "aria-live": showDismissButton ? undefined : 'polite', "aria-atomic": showDismissButton ? undefined : true }, visible && triggerRef.current && (react_1.default.createElement(core_2.Popover, { anchorEl: triggerRef.current, open: visible, onClose: onPopoverClose, anchorOrigin: anchorOrigin, transformOrigin: transformOrigin, style: variant === 'hover' ? { pointerEvents: 'none' } : undefined },
            react_1.default.createElement("div", { className: classes.container },
                react_1.default.createElement("div", { className: clsx_1.default(classes.body, classes[`body-size-${size}`], {
                        'fixed-width': fixedWidth,
                    }), role: "dialog", "aria-modal": showDismissButton ? true : undefined, "aria-labelledby": showDismissButton && header ? labelledById : undefined },
                    header && (react_1.default.createElement("div", { className: showDismissButton ? classes.hasDismiss : undefined },
                        dismissButton,
                        react_1.default.createElement("div", { className: classes.header, id: labelledById },
                            react_1.default.createElement("h2", null, header)))),
                    react_1.default.createElement("div", { className: !header && showDismissButton ? classes.hasDismiss : undefined },
                        !header && dismissButton,
                        react_1.default.createElement("div", { className: classes.content }, content)))))))));
};
exports.default = Popover;
//# sourceMappingURL=index.js.map